import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import type { Model } from '../data/models'
import { resolveModelIdAliases } from '../data/brands'
import { useAuth } from './AuthContext'
import { useNotifications } from './NotificationContext'
import { useRecentGenerationsContext } from './RecentGenerationsContext'
import { useSelectedModel } from './SelectedModelContext'
import { applyCardSettings } from '../pages/generate/logic/applyCardSettings'
import { cardMatchesModel, getAttachedStyleCard, subscribeStyleCardDetached, useAttachedStyleCard } from '../api/attachedStyleCard'
import { markSceneChannelsReset } from '../api/sceneSelection'
import { genVersionStore } from '../pages/bots/components/genVersionStore'
import {
	userSettingParam,
	useGenerateSettings,
	type GenerateSettings,
} from '../pages/generate/hooks/useGenerateSettings'
import { publishPresentationSettings } from '../pages/generate/utils/presentationPreviewStore'
import { useGenerateUploads } from '../pages/generate/hooks/useGenerateUploads'
import { useGenerateCost } from '../pages/generate/hooks/useGenerateCost'
import { getImageInput, splitKnownInputSeconds, videoInputsOf } from '../pages/generate/utils/generatePayloadBuilders'
import { useInputVideoSeconds } from '../pages/generate/hooks/useInputVideoSeconds'
import { billableInputSeconds, inputVideoRate } from '../utils/modelCost'
import { WAN_MAX_INPUT_PLUS_OUTPUT_SECONDS, isWan3Model } from '../utils/frameRefModes'
import { useDraftInfo, type DraftInfo } from '../pages/generate/hooks/useDraftInfo'
import { isSeedance25Model } from '../pages/generate/utils/referenceLimits'
import { useGenerationFlow } from '../pages/generate/hooks/useGenerationFlow'
import { useMotionPrecheck, type MotionRisk } from '../pages/generate/hooks/useMotionPrecheck'
import { usePresentationEstimate } from '../pages/generate/hooks/usePresentationEstimate'
import { getModelConfig } from '../pages/generate/utils/generateModelConfig'
import { GENERATE_PROMPT_KEY, PERSISTED_UPLOADS_KEY, UPLOADS_EXPIRY_MS } from '../pages/generate/utils/constants'
import type { InputsSnapshot } from '../pages/generate/logic/inputsSnapshot'
import type { UploadedFile, UploadedVideo } from '../pages/generate/utils/uploadHelpers'
import type { SunoTrack } from '../api/music'

// ── Audio operation mode ──────────────────────────────────────────
// 'generate'         — обычная генерация (track_id не нужен)
// 'extend'           — продление существующего трека
// 'cover'            — cover-версия существующего трека
// 'add_vocals'       — добавить вокал к инструментальному треку
// 'add_instrumental' — добавить инструментал к вокальному треку
export type AudioOperation = 'generate' | 'extend' | 'cover' | 'add_vocals' | 'add_instrumental'

// ── Снапшот загрузок между экранами ──────────────────────────────
// Единственный мост между разными экземплярами GenerateProvider: виджет
// /bots/* держит свой инстанс, App-уровневый — свой (см. App.tsx). Хранит
// только url'ы: File не сериализуется, а на сервере файл уже лежит.
interface PersistedUploads {
	uploadedImageUrl?: string | null
	swapImageUrl?: string | null
	uploadedVideoUrl?: string | null
	referenceImageUrls?: string[]
	omniReferenceImageUrls?: string[]
	modelId?: string | null
	prompt?: string
	savedAt: number
}

/** Читает снапшот, проверяя TTL. Протухший и битый — удаляет и отдаёт null. */
function readPersistedUploads(): PersistedUploads | null {
	try {
		const raw = localStorage.getItem(PERSISTED_UPLOADS_KEY)
		if (!raw) return null
		const data = JSON.parse(raw) as PersistedUploads
		if (!data || Date.now() - data.savedAt > UPLOADS_EXPIRY_MS) {
			localStorage.removeItem(PERSISTED_UPLOADS_KEY)
			return null
		}
		return data
	} catch {
		localStorage.removeItem(PERSISTED_UPLOADS_KEY)
		return null
	}
}

/** url'ы → UploadedFile. `file` осознанно пуст: исходного File у нас нет,
 *  и он не нужен — вся отправка идёт по url. Превью тоже url, чтобы не
 *  зависеть от blob'а, отозванного в другом экземпляре провайдера. */
function urlsToUploadedFiles(urls: string[] | undefined): UploadedFile[] {
	return (urls ?? []).map((url) => ({ file: null as unknown as File, preview: url, url, restored: true }))
}

// ── Types ────────────────────────────────────────────────────────

type MultiShot = { prompt: string; duration: number }

interface GenerateContextValue {
	// ── Model ──────────────────────────────────────────────────
	selectedModel: Model | null
	setSelectedModel: (m: Model | null) => void

	// ── Prompt ─────────────────────────────────────────────────
	prompt: string
	setPrompt: (v: string) => void

	// ── Settings ───────────────────────────────────────────────
	settings: GenerateSettings
	setSettings: (updater: (prev: GenerateSettings) => GenerateSettings) => void
	updateSetting: <K extends keyof GenerateSettings>(key: K, value: GenerateSettings[K]) => void
	/** То же, что updateSetting, но от действия пользователя (пилюли, шторка): отмечает настройку как изменённую им —
	 *  для блока «Тренд» в админке. Программные подстройки идут через updateSetting и отметки не получают. */
	userUpdateSetting: <K extends keyof GenerateSettings>(key: K, value: GenerateSettings[K]) => void
	resetSettings: (model: Model) => void

	// ── Uploads ────────────────────────────────────────────────
	isUploading: boolean
	setIsUploading: (v: boolean) => void
	/** Есть итем без .url — загрузка реально не завершена. Надёжнее
	 *  isUploading при параллельных загрузках (см. useGenerateUploads). */
	hasPendingUploads: boolean
	isDragging: boolean
	uploadedImage: UploadedFile | null
	referenceImages: UploadedFile[]
	/** Именованные фото-слоты карточки: позиция = смысл, null = пусто;
	 *  referenceImages зеркалит их плотным массивом (см. useGenerateUploads). */
	slotImages: (UploadedFile | null)[]
	/** Именованные фото-слоты карточки активны (UI-гейты: пилюля режима Seedance). */
	photoSlotsEngaged: boolean
	handleSlotImageUpload: (
		idx: number,
		e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } },
	) => void
	removeSlotImage: (idx: number) => void
	/** Плейсхолдер в слоте на время скачивания из галереи; null — убрать. */
	setSlotImagePending: (idx: number, preview: string | null) => void
	/** Мультистиль: активный набор номеров слотов (фильтр зеркала referenceImages). */
	setActiveSlotNums: (nums: number[] | null) => void
	swapImage: UploadedFile | null
	uploadedVideo: UploadedVideo | null
	endImage: UploadedFile | null
	omniReferenceImages: UploadedFile[]
	referenceVideo: UploadedVideo | null
	referenceVideos: UploadedVideo[]
	referenceAudios: UploadedFile[]
	selectedVideoDuration: number
	/** Kling Motion Control: «Kling, скорее всего, откажет» по ролику (замер позы на сервере) — строка над промптом и окно
	 *  перед запуском; null — предупреждать не о чем или модель другая. */
	motionRisk: MotionRisk | null
	fileInputRef: React.RefObject<HTMLInputElement | null>
	swapInputRef: React.RefObject<HTMLInputElement | null>
	videoInputRef: React.RefObject<HTMLInputElement | null>
	endImageInputRef: React.RefObject<HTMLInputElement | null>
	refImagesInputRef: React.RefObject<HTMLInputElement | null>
	refVideoInputRef: React.RefObject<HTMLInputElement | null>
	refVideosInputRef: React.RefObject<HTMLInputElement | null>
	refAudiosInputRef: React.RefObject<HTMLInputElement | null>
	handleImageUpload: (
		e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } },
	) => void
	handleMultipleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
	handleSwapImageUpload: (
		e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } },
	) => void
	handleVideoUpload: (
		e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } },
	) => void
	handleEndImageUpload: (
		e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } },
	) => void
	handleOmniRefImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
	handleReferenceVideoUpload: (
		e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } },
	) => void
	handleReferenceVideosUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
	handleReferenceAudiosUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
	audioTrimFile: File | null
	applyTrimmedAudio: (trimmedFile: File, duration: number) => Promise<void>
	cancelAudioTrim: () => void
	/** Тример видео-референса (25.09): ссылка ролика длиннее лимита модели; отрезок выбирает юзер. */
	videoTrimUrl: string | null
	applyTrimmedVideo: (trimmedUrl: string, duration: number) => void
	cancelVideoTrim: () => void
	applyDefaultReferenceVideo: (url: string) => Promise<void>
	applyDefaultReferenceVideos: (url: string) => void
	applyDefaultReferenceAudio: (url: string) => void
	applyDefaultUploadedVideo: (url: string) => Promise<void>
	applyDefaultUploadedImage: (url: string) => Promise<void>
	dragProps: (
		handler: (e: React.ChangeEvent<HTMLInputElement> | { target: { files: File[] } }) => void,
	) => object
	setUploadedImage: (v: UploadedFile | null) => void
	setSwapImage: (v: UploadedFile | null) => void
	setUploadedVideo: (v: UploadedVideo | null) => void
	setEndImage: (v: UploadedFile | null) => void
	setReferenceImages: (updater: (prev: UploadedFile[]) => UploadedFile[]) => void
	setOmniReferenceImages: (updater: (prev: UploadedFile[]) => UploadedFile[]) => void
	setReferenceVideo: (v: UploadedVideo | null) => void
	setReferenceVideos: (updater: (prev: UploadedVideo[]) => UploadedVideo[]) => void
	setReferenceAudios: (updater: (prev: UploadedFile[]) => UploadedFile[]) => void
	setSelectedVideoDuration: (v: number) => void
	resetUploads: () => void
	/** Сбросить только референсы карточки «под капотом», фото пользователя оставить (смена участника мульти-тренда). */
	resetCardRefs: () => void
	/** Вернуть входы из снимка стикера — каждый канал на своё место (см. inputsSnapshot.ts). */
	restoreInputs: (snapshot: InputsSnapshot) => Promise<void>
	/** Счётчик сбросов загрузок (resetUploads): по нему tool-режим переподставляет админские референсы. */
	uploadsEpoch: number

	// ── Cost ───────────────────────────────────────────────────
	currentCost: number
	/** Плата за секунды входного видео: сервер меряет видео на входе — цену со входом ещё не знаем, запуск ждёт. */
	costPending: boolean
	/** Видео на входе не измерилось — цену не посчитать, запуск закрыт с этим текстом. */
	costIssue: string | null
	/** Черновик Seedance 2.5 (проба 480p → финал 1080p): ответ сервера о доступности и ценах для текущей длительности. */
	draftInfo: DraftInfo
	/** Переключатель «Черновик» можно показывать: сервер отдаёт черновики, нет карточки-инструмента и видео-/аудио-референса. */
	draftAvailable: boolean

	// ── Generation flow ────────────────────────────────────────
	isLoading: boolean
	error: string | null
	result: string | null
	resultUrls: string[] | null
	/** Обложки параллельно с resultUrls — только для audio. null для не-audio. */
	resultImageUrls: string[] | null
	lastRequestId: string | null
	userRating: number | null
	handleGenerate: () => void
	/** «Продолжить в фоне»: идущая генерация уходит в стикер, виджет свободен для следующей. */
	detachGeneration: () => void
	resetResult: () => void
	/** Вернуть конкретный результат в зону (возврат по стикеру генерации). */
	restoreResult: (payload: { result: string; resultUrls?: string[] | null; requestId?: string | null }) => void
	/** Функцией — снять только свою плашку (отказ подготовки фото), не чужую ошибку. */
	setError: React.Dispatch<React.SetStateAction<string | null>>
	clearError: () => void
	/** Выставить результат извне: страница генерации так «догоняет» генерацию,
	 *  которая дошла до бэка вопреки обрыву связи (см. Generate.tsx). */
	setResult: (url: string | null) => void
	setUserRating: (r: number | null) => void

	// ── UI state ───────────────────────────────────────────────
	negativePromptOpen: boolean
	setNegativePromptOpen: (v: boolean) => void
	multiShots: MultiShot[]
	setMultiShots: React.Dispatch<React.SetStateAction<MultiShot[]>>
	showStyleHint: boolean
	setShowStyleHint: (v: boolean) => void

	// ── Model config (derived) ─────────────────────────────────
	isVideo: boolean
	isAudio: boolean
	/** selectedModel?.category === 'presentation' (Umnik Slides) */
	isPresentation: boolean
	requiresVideo: boolean
	requiresImage: boolean
	/** Модель принимает изображение опционально (не обязательно) */
	supportsImageInput: boolean
	requiresTwoImages: boolean
	supportsMultipleImages: boolean

	// ── Audio source-track mode ────────────────────────────────
	/** Исходный трек для операций extend/cover/add_vocals/add_instrumental.
	 *  null = обычная генерация с нуля. */
	sourceTrack: SunoTrack | null
	audioOperation: AudioOperation
	/** Установить source-трек. Если передать null — режим сбрасывается
	 *  в 'generate'. При вызове с треком — operation обязателен. */
	setSourceTrack: (track: SunoTrack | null, operation?: AudioOperation) => void

	// ── Utilities ──────────────────────────────────────────────
	/** Сбросить все состояния (промт, загрузки, результат, настройки) */
	resetAll: () => void
}

// ── Context ──────────────────────────────────────────────────────

const GenerateContext = createContext<GenerateContextValue | null>(null)

// eslint-disable-next-line react-refresh/only-export-components
export function useGenerate(): GenerateContextValue {
	const ctx = useContext(GenerateContext)
	if (!ctx) throw new Error('useGenerate must be used inside <GenerateProvider>')
	return ctx
}

// ── Provider ─────────────────────────────────────────────────────

interface GenerateProviderProps {
	children: React.ReactNode
	/** Текущая модель (управляется снаружи) */
	model: Model | null
	/** Коллбек при смене модели изнутри виджета */
	onModelChange?: (m: Model | null) => void
}

export function GenerateProvider({ children, model, onModelChange }: GenerateProviderProps) {
	const { user, refreshUser } = useAuth()
	const { addNotification } = useNotifications()
	const { addGeneration } = useRecentGenerationsContext()
	const { prompt: ctxPrompt, setPrompt: setCtxPrompt, setIsGenerating, pendingSettings, setPendingSettings, pendingModelId, setPendingModelId } = useSelectedModel()

	// ── Model ─────────────────────────────────────────────────
	const [selectedModel, setSelectedModelState] = useState<Model | null>(model)

	// Синхронизация с внешним пропом при смене модели
	useEffect(() => {
		setSelectedModelState(model)
		// Если ушли с audio — source-трек больше не имеет смысла.
		// Сеттеры объявлены ниже — TDZ-safe (эффект исполняется после маунта).
		if (model && model.category !== 'audio') {
			setSourceTrackState(null)
			setAudioOperation('generate')
		}
	}, [model?.id]) // eslint-disable-line react-hooks/exhaustive-deps

	const setSelectedModel = useCallback(
		(m: Model | null) => {
			setSelectedModelState(m)
			onModelChange?.(m)
		},
		[onModelChange],
	)

	// ── Prompt ────────────────────────────────────────────────
	const [prompt, setPromptState] = useState(
		() => localStorage.getItem(GENERATE_PROMPT_KEY) || '',
	)

	const setPrompt = useCallback(
		(v: string) => {
			setPromptState(v)
			setCtxPrompt(v)
			if (v) {
				localStorage.setItem(GENERATE_PROMPT_KEY, v)
			} else {
				localStorage.removeItem(GENERATE_PROMPT_KEY)
			}
		},
		[setCtxPrompt],
	)

	// Читаем промт из SelectedModelContext (home page → generate page)
	useEffect(() => {
		if (ctxPrompt.trim()) {
			setPromptState(ctxPrompt)
			localStorage.setItem(GENERATE_PROMPT_KEY, ctxPrompt)
			setCtxPrompt('')
		}
	}, [ctxPrompt, setCtxPrompt])

	// Карточка-инструмент откреплена (крестик GTM / уход со страницы / смена
	// модели) → её «промпт под капотом» гасим В КАЖДОМ инстансе провайдера.
	// resetAll крестика чистит только СВОЙ инстанс, а App-уровневый провайдер
	// переживает SPA-переходы и уже потребил промпт карточки через ctxPrompt-
	// синк — скрытый рецепт всплывал в поле /generate (утечка промптов).
	// Свободный текст юзера не трогаем: карточки без tool-режима и без
	// promptLocked подставляют промпт видимым — юзер мог его редактировать.
	useEffect(
		() =>
			subscribeStyleCardDetached((card) => {
				// Отметки «изменил пользователь» — про этот тренд (блок «Тренд» в админке): тренд снят — снимаем и их,
				// иначе при новом открытии того же тренда (настройки пина второй раз не применяются) уехали бы старые.
				setSettings((prev) => (prev.userChanged ? { ...prev, userChanged: undefined } : prev))
				if (!card.toolMode && !card.promptLocked) return
				setPromptState('')
				localStorage.removeItem(GENERATE_PROMPT_KEY)
			}),
		[], // eslint-disable-line react-hooks/exhaustive-deps
	)

	// ── UI state ──────────────────────────────────────────────
	const [negativePromptOpen, setNegativePromptOpen] = useState(false)

	// Закрываем negative-prompt панель при смене модели. Иначе она «прилипает»
	// и торчит на моделях которые negative prompt не поддерживают (юзер открыл
	// на video-модели → перешёл на image-модель — окошко осталось висеть).
	// Сам текст (settings.negativePrompt) НЕ чистим — если юзер вернётся к
	// прежней модели, его negative prompt сохранится.
	useEffect(() => {
		setNegativePromptOpen(false)
	}, [selectedModel?.id])
	const [multiShots, setMultiShots] = useState<MultiShot[]>([])
	const [showStyleHint, setShowStyleHint] = useState(false)

	// ── Settings ──────────────────────────────────────────────
	// Объявляем ДО setSourceTrack — он использует setSettings для принудительных
	// значений при переключении операции (audio_instrumental для add_*).
	const { settings, setSettings, updateSetting, resetSettings } =
		useGenerateSettings(selectedModel)
	const userUpdateSetting = <K extends keyof GenerateSettings>(key: K, value: GenerateSettings[K]) => {
		updateSetting(key, value)
		if (!userSettingParam(key)) return
		setSettings((prev) => (prev.userChanged?.includes(key) ? prev : { ...prev, userChanged: [...(prev.userChanged ?? []), key] }))
	}

	// Зеркало настроек презентаций для превью-слайда ВНЕ провайдера (страница
	// /bots/tools/slides и её виджет живут в разных GenerateProvider'ах —
	// см. presentationPreviewStore). Публикуют все провайдеры; побеждает
	// последний изменившийся — на странице слайдов это виджет.
	useEffect(() => {
		publishPresentationSettings(settings.presentation)
	}, [settings.presentation])

	// Пресет параметров с карточки popular_category (home → /generate): применяем
	// один раз ПОСЛЕ того, как выбранная модель догналась до целевой (pendingModelId),
	// и очищаем канал.
	//
	// Почему нельзя применять раньше: GenerateProvider живёт на уровне App, а смена
	// модели прокидывается через промежуточный эффект (model prop → local state),
	// т.е. на более позднем коммите, чем установка pendingSettings. model-sync внутри
	// useGenerateSettings сбрасывает duration/resolution/quality к дефолтам модели
	// именно на этом позднем коммите — и затирал бы уже применённый пресет (Seedance
	// отдавал durations[0]=3с вместо заданных 10с, Kling — дефолты «без параметров»).
	//
	// Ключ эффекта включает selectedModel?.id → на коммите смены модели он
	// исполняется ПОСЛЕ model-sync (тот зарегистрирован раньше — в useGenerateSettings),
	// поэтому пресет ложится поверх сброса. Гейт по совпадению модели гарантирует,
	// что мы не применим/не очистим пресет на промежуточной (старой) модели.
	useEffect(() => {
		if (!pendingSettings) return
		// Совпадение целевой модели: сравниваем с учётом alias'ов id/backendModel
		// (тот же механизм, что и deep-link ?model= на /generate).
		const matchesTarget =
			!pendingModelId ||
			(!!selectedModel &&
				resolveModelIdAliases(pendingModelId).some(
					(id) => id === selectedModel.id || id === selectedModel.backendModel,
				))
		if (!selectedModel || !matchesTarget) return
		// Новый тренд — отметки «изменил пользователь» с чистого листа (до применения его настроек).
		updateSetting('userChanged', undefined)
		applyCardSettings(pendingSettings, updateSetting)
		setPendingSettings(null)
		setPendingModelId(null)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		pendingSettings,
		pendingModelId,
		selectedModel?.id,
		updateSetting,
		setPendingSettings,
		setPendingModelId,
	])

	// ── Audio source-track mode (extend/cover/add_vocals/add_instrumental) ─
	// В localStorage не кладём — это ephemeral режим на одну генерацию.
	// После успешной генерации или ручного закрытия pin'а сбрасываем обратно.
	const [sourceTrack, setSourceTrackState] = useState<SunoTrack | null>(null)
	const [audioOperation, setAudioOperation] = useState<AudioOperation>('generate')

	const setSourceTrack = useCallback(
		(track: SunoTrack | null, operation: AudioOperation = 'generate') => {
			setSourceTrackState(track)
			const nextOp: AudioOperation = track ? operation : 'generate'
			setAudioOperation(nextOp)
			// При переходе в режим операции над треком — гарантируем music submode.
			// Sounds mode по API работает только без source-track.
			if (track) {
				setSettings((prev) => {
					const next = { ...prev, audioSubmode: 'music' as const }
					// Принудительные значения для некоторых операций (гайд Suno).
					// add_instrumental: операция сама добавляет инструментал →
					//   instrumental в payload должен быть true.
					// add_vocals: добавляем голос к инструменталу →
					//   instrumental должен быть false.
					if (nextOp === 'add_instrumental') {
						next.audioInstrumental = true
					} else if (nextOp === 'add_vocals') {
						next.audioInstrumental = false
					}
					return next
				})
			}
		},
		[setSettings],
	)

	// ── Model config ──────────────────────────────────────────
	const modelConfig = getModelConfig(selectedModel)
	const { isVideo, requiresVideo, requiresImage, supportsImageInput, requiresTwoImages, supportsMultipleImages } =
		modelConfig
	const isAudio = selectedModel?.category === 'audio'
	const isPresentation = selectedModel?.category === 'presentation'

	// ── Error ref (передаём в useGenerateUploads) ─────────────
	const setErrorRef = useRef<React.Dispatch<React.SetStateAction<string | null>>>(() => {})

	// ── Uploads ───────────────────────────────────────────────
	const uploads = useGenerateUploads(
		selectedModel,
		(err) => setErrorRef.current(err),
		setSettings,
		settings,
	)

	// ── Restore uploads from localStorage on mount ────────────
	// Allows state to survive navigation away and sync between dialogs
	useEffect(() => {
		const data = readPersistedUploads()
		if (!data) return
		if (data.uploadedImageUrl) {
			const url: string = data.uploadedImageUrl
			uploads.setUploadedImage({ file: null as unknown as File, preview: url, url, restored: true })
		}
		if (data.swapImageUrl) {
			const url: string = data.swapImageUrl
			uploads.setSwapImage({ file: null as unknown as File, preview: url, url, restored: true })
		}
		if (data.uploadedVideoUrl) {
			const url: string = data.uploadedVideoUrl
			uploads.setUploadedVideo({ file: null as unknown as File, preview: url, url, restored: true })
		}
		if (data.referenceImageUrls?.length) {
			uploads.setReferenceImages(() => urlsToUploadedFiles(data.referenceImageUrls))
		}
		if (data.omniReferenceImageUrls?.length) {
			uploads.setOmniReferenceImages(() => urlsToUploadedFiles(data.omniReferenceImageUrls))
		}
		if (data.prompt) {
			setPromptState(data.prompt)
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// ── Дотягиваем референсы при SPA-переходе ─────────────────
	// Эффект выше срабатывает ТОЛЬКО на mount, а App-уровневый GenerateProvider
	// за время жизни SPA не перемонтируется (см. App.tsx) — снапшот он больше
	// никогда не перечитывал. Виджет /bots/* при этом живёт в СВОЁМ экземпляре
	// провайдера, и его state App-уровневому не виден вообще. Поэтому переход
	// «виджет → страница нейросети» приходил с пустыми слотами, хотя промпт и
	// модель доезжали: прикреплённое фото просто некому было передать.
	//
	// Три предохранителя, чтобы чужое фото не «протекло» в новый контекст:
	//   1) канал восстанавливаем только если он ПУСТ — свежий выбор юзера
	//      всегда важнее снапшота;
	//   2) модель снапшота должна совпадать с текущей, с учётом алиасов
	//      id/backendModel (тот же resolveModelIdAliases, что у ?model=);
	//   3) TTL снапшота (UPLOADS_EXPIRY_MS) — внутри readPersistedUploads.
	// Снятие фото пользователем перезаписывает снапшот пустым, так что вернуть
	// удалённое этот эффект не может.
	const location = useLocation()
	useEffect(() => {
		if (!selectedModel) return
		const data = readPersistedUploads()
		if (!data) return
		if (data.modelId) {
			const aliases = resolveModelIdAliases(data.modelId)
			const sameModel = aliases.some(
				(id) => id === selectedModel.id || id === selectedModel.backendModel,
			)
			if (!sameModel) return
		}
		if (data.omniReferenceImageUrls?.length) {
			uploads.setOmniReferenceImages((prev) =>
				prev.length ? prev : urlsToUploadedFiles(data.omniReferenceImageUrls),
			)
		}
		if (data.referenceImageUrls?.length) {
			uploads.setReferenceImages((prev) =>
				prev.length ? prev : urlsToUploadedFiles(data.referenceImageUrls),
			)
		}
		if (data.uploadedImageUrl && !uploads.uploadedImage) {
			const url: string = data.uploadedImageUrl
			uploads.setUploadedImage({ file: null as unknown as File, preview: url, url, restored: true })
		}
	}, [location.pathname, selectedModel?.id]) // eslint-disable-line react-hooks/exhaustive-deps

	// ── Persist uploads to localStorage when they change ──────
	// Кладём ТОЛЬКО url'ы: File в localStorage не сериализуется, да и не нужен —
	// на сервер файл уже уехал (uploadAndGetUrl). Пока картинка грузится, url
	// пуст, и в снапшот она не попадает: это осознанно, недогруженный файл
	// восстанавливать нечем.
	//
	// referenceImages / omniReferenceImages добавлены 20.08.2026: раньше
	// сохранялись только три одиночных канала, поэтому мульти-референсы
	// (в т.ч. слоты Gemini Omni) терялись гарантированно.
	const refImageUrlsKey = uploads.referenceImages.map((f) => f.url ?? '').join('|')
	const omniRefUrlsKey = uploads.omniReferenceImages.map((f) => f.url ?? '').join('|')
	const uploadsInitRef = useRef(false)
	useEffect(() => {
		if (!uploadsInitRef.current) {
			uploadsInitRef.current = true
			return // skip initial mount render — values are pre-restore defaults
		}
		const imageUrl = uploads.uploadedImage?.url ?? null
		const swapUrl = uploads.swapImage?.url ?? null
		const videoUrl = uploads.uploadedVideo?.url ?? null
		const refImageUrls = uploads.referenceImages
			.map((f) => f.url)
			.filter((u): u is string => !!u)
		const omniRefUrls = uploads.omniReferenceImages
			.map((f) => f.url)
			.filter((u): u is string => !!u)
		if (!imageUrl && !swapUrl && !videoUrl && !refImageUrls.length && !omniRefUrls.length) {
			localStorage.removeItem(PERSISTED_UPLOADS_KEY)
		} else {
			localStorage.setItem(
				PERSISTED_UPLOADS_KEY,
				JSON.stringify({
					uploadedImageUrl: imageUrl,
					swapImageUrl: swapUrl,
					uploadedVideoUrl: videoUrl,
					referenceImageUrls: refImageUrls,
					omniReferenceImageUrls: omniRefUrls,
					modelId: selectedModel?.id ?? null,
					savedAt: Date.now(),
				}),
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		uploads.uploadedImage?.url,
		uploads.swapImage?.url,
		uploads.uploadedVideo?.url,
		refImageUrlsKey,
		omniRefUrlsKey,
	])

	// ── Presentation estimate (динамическая цена для category='presentation') ─
	// Debounced POST /presentations/estimate на каждое изменение полей.
	// Бэк считает по содержимому PPTX (edit) или числу слайдов (create) +
	// image_count. Результат передаётся в useGenerateCost ниже, который
	// для presentation-моделей возвращает его вместо базовой цены.
	// Для Gamma цена считается на фронте (useGenerateCost → формула
	// max(num_cards × 25, 200)). Backend /estimate рассчитан на Umnik
	// Slides (Claude-токены) — для Gamma его вызывать не нужно.
	const isGammaModel = selectedModel?.backendModel === 'gamma-presentation'
	const presEstimate = usePresentationEstimate({
		mode: settings.presentation?.mode ?? 'edit',
		prompt,
		fileUrl: settings.presentation?.fileUrl,
		slideCount: settings.presentation?.slideCount,
		imageCount: settings.presentation?.imageUrls?.length ?? 0,
		enabled: isPresentation && !isGammaModel,
	})

	// ── Cost ──────────────────────────────────────────────────
	const hasImage = !!(uploads.uploadedImage?.url || uploads.referenceImages?.some((r) => r.url))
	// Сколько фото уйдёт в запрос — той же функцией, что собирает image_input при запуске: за каждое у моделей
	// со строкой «input_photo» надбавка к цене (modelCost.photoSurcharge), как спишет бэк.
	const imageCount = getImageInput({
		requiresTwoImages,
		supportsMultipleImages,
		uploadedImage: uploads.uploadedImage,
		swapImage: uploads.swapImage,
		referenceImages: uploads.referenceImages,
	})?.length ?? 0
	const hasVideo = !!(
		uploads.uploadedVideo?.url ||
		uploads.referenceVideo?.url ||
		uploads.referenceVideos?.some((v) => v.url)
	)
	// Плата за секунды входного видео (выключатель «Правила цены», backend services/input_video_billing): какие видео уйдут
	// в запрос — тем же правилом, что запрос (videoInputsOf), их секунды меряет сервер. Пока меряем — цену со входом не
	// показываем и запуск ждёт: сервер спишет по своему замеру, не больше показанного.
	const ioCard = useAttachedStyleCard()
	const ioInputs = selectedModel && inputVideoRate(selectedModel, settings) != null
		? videoInputsOf(selectedModel, settings, uploads.uploadedVideo, uploads.referenceVideo, uploads.referenceVideos)
		: null
	// Отрезок «Повторить движение» (billedSeconds) сервер получит ровно такой длины — его не меряем, секунды известны.
	const ioSplit = splitKnownInputSeconds(ioInputs?.ownUrls ?? [], [uploads.uploadedVideo, uploads.referenceVideo, ...(uploads.referenceVideos ?? [])])
	const ioOwnKey = ioSplit.measure.join('\n')
	const ioOwnUrls = useMemo(() => (ioOwnKey ? ioOwnKey.split('\n') : []), [ioOwnKey])
	const ioKnownKey = ioSplit.known.join(',')
	const ioKnown = useMemo(() => (ioKnownKey ? ioKnownKey.split(',').map(Number) : []), [ioKnownKey])
	const ioTrendSource = ioInputs?.trendVideo ? (ioCard?.id ?? null) : null
	const ioTrendUnknown = !!ioInputs?.trendVideo && !ioCard?.id
	// Гость: ручки замера требуют входа (401 увёл бы на /login) — запросов нет. Цена — по длине видео тренда из
	// витрины (как на плитке), без неё — как раньше; запустить гость не может — кнопка ведёт ко входу.
	const ioGuest = !user
	const ioGuestSeconds = ioGuest && ioInputs?.trendVideo && !ioOwnUrls.length
		? billableInputSeconds([ioCard?.refVideoSeconds ?? null])
		: null
	const ioOn = !!ioInputs && (ioGuest ? ioGuestSeconds != null : ioOwnUrls.length > 0 || ioKnown.length > 0 || ioInputs.trendVideo)
	const inputVideo = useInputVideoSeconds(ioOn && !ioGuest, ioOwnUrls, ioTrendSource, ioKnown)
	const costPending = ioOn && !ioGuest && inputVideo.pending
	// Wan 3.0: секунды видео на входе + длительность ролика — не больше 30 (KIE). Сервер откажет тем же текстом до списания
	// (backend services/wan_refs) — говорим заранее и не пускаем запуск.
	const wanInputSeconds = ioOn && !ioGuest && isWan3Model(selectedModel) ? inputVideo.seconds : null
	const wanOutputSeconds = Number(settings.duration) || 0
	const wanTooLong = wanInputSeconds != null && wanInputSeconds + wanOutputSeconds > WAN_MAX_INPUT_PLUS_OUTPUT_SECONDS
	const costIssue = ioOn && !ioGuest && (inputVideo.failed || ioTrendUnknown)
		? 'Не удалось измерить видео на входе — без его длины цену не посчитать. Попробуйте загрузить видео ещё раз.'
		: wanTooLong
			? `Видео на входе (${wanInputSeconds} с) и ролик (${wanOutputSeconds} с) вместе длиннее ${WAN_MAX_INPUT_PLUS_OUTPUT_SECONDS} с — Wan 3.0 так не принимает. Уменьшите длительность до ${WAN_MAX_INPUT_PLUS_OUTPUT_SECONDS - (wanInputSeconds as number)} с или возьмите отрезок видео короче.`
			: null

	const computedCost = useGenerateCost({
		selectedModel,
		settings,
		requiresVideo,
		selectedVideoDuration: uploads.selectedVideoDuration,
		uploadedVideoDuration: uploads.uploadedVideo?.duration,
		// В режиме параметрических операций (extend/cover/+vocals/+instr)
		// цена считается по другому variant'у — не базовая «generate».
		audioOperation,
		presentationEstimate: presEstimate.creditsPrice,
		hasImage,
		hasVideo: hasVideo || ioOn,
		imageCount,
		referenceVideos: uploads.referenceVideos,
		inputVideoSeconds: ioOn ? (ioGuest ? ioGuestSeconds : inputVideo.seconds) : null,
	})
	// «Своя цена карточки» (MONEY-PATH-отображение): пока прикреплена карточка
	// с оверрайдом, показываем floor-цену max(расчётная, оверрайд) — ровно то,
	// что спишет бэк (кламп в get_credits_price/get_video_credits_price).
	// ИСКЛЮЧЕНИЕ — живая акция (discountUntil в будущем): priceOverride несёт
	// ПРОМО-цену, скидки-уровнями она честно НИЖЕ базы и бэк спишет её же
	// (effective_source_price; промо действует только на родную модель
	// карточки — а пин авто-детачится при смене модели, каналы согласованы).
	// Акция истекла с висящим пином → снова floor, как спишет бэк по UTC.
	//
	// Гейт regularPrice: юзер может выбрать настройки ДОРОЖЕ пресета карточки
	// (качество/разрешение/длительность). Бэк тогда считает себестоимость по
	// НОВЫМ настройкам и, если промо её не покрывает (< реф-пола), списывает
	// полную базу. Себестоимость фронту неизвестна (и не должна утекать),
	// поэтому правило консервативное: расчёт вышел за обычную цену источника
	// → показываем floor-цену. Так витрина НИКОГДА не ниже списания; если бэк
	// всё же применит промо — юзер заплатит меньше показанного, не больше.
	const attachedCard = useAttachedStyleCard()
	// Черновик Seedance 2.5: BytePlus не принимает в него видео и аудио, а карточка-инструмент подставляет свои референсы и
	// цену — с любым из них переключатель прячется (сервер отказал бы тем же). Видео и аудио считаются только в режиме
	// «Референсы»: в «Кадрах» они в запрос не уходят.
	const draftCapable = isSeedance25Model(selectedModel)
	// Без входа сервер не спрашиваем: ответ 401 уводит на /login (api/client), и гость с выбранной Seedance 2.5 оказался бы
	// на странице входа. Гость и так не может запустить генерацию — переключатель ему ни к чему.
	const draftInfo = useDraftInfo(draftCapable && !!user, settings.duration, !!settings.sound)
	const draftMediaBlocks =
		(settings.seedanceMode ?? 'frames') === 'references' && (hasVideo || (uploads.referenceAudios?.length ?? 0) > 0)
	const draftAvailable = draftCapable && draftInfo.available && !attachedCard && !draftMediaBlocks
	// Включённый черновик стоит свою цену (480p, строка «Адаптеров»), а не цену выбранного разрешения.
	const draftOn = draftAvailable && settings.draft === true && draftInfo.draftCredits != null
	const promoLive = !!attachedCard?.discountUntil &&
		new Date(attachedCard.discountUntil).getTime() > Date.now()
	const promoApplies = promoLive &&
		(attachedCard?.regularPrice == null || computedCost <= attachedCard.regularPrice)
	const currentCost = draftOn
		? (draftInfo.draftCredits as number)
		: attachedCard?.priceOverride
			? (promoApplies ? attachedCard.priceOverride : Math.max(computedCost, attachedCard.priceOverride))
			: computedCost

	// Пресет настроек прикреплённой карточки: пин переживает F5 (sessionStorage),
	// а настройки жили только в state — после reload инструмент открывался с
	// последними настройками юзера из localStorage (и ценой от них) вместо
	// пресета админа. Применяем settings пина, когда выбранная модель догналась
	// до целевой (эффект зарегистрирован ПОСЛЕ model-sync в useGenerateSettings —
	// сброс к дефолтам модели нас не перетрёт). Один раз на пин (ref-гейт):
	// ручные правки настроек юзера при открытом инструменте не трогаем.
	const appliedPinSettingsRef = useRef<string | null>(null)
	useEffect(() => {
		if (!attachedCard?.settings || !attachedCard.id) return
		if (appliedPinSettingsRef.current === attachedCard.id) return
		if (!selectedModel || !cardMatchesModel(attachedCard, selectedModel)) return
		appliedPinSettingsRef.current = attachedCard.id
		updateSetting('userChanged', undefined)
		applyCardSettings(attachedCard.settings, updateSetting)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [attachedCard, selectedModel?.id])

	// ── Flow ──────────────────────────────────────────────────
	//
	// Сохранение в историю теперь network-level: useGenerationFlow сам
	// строит snapshot в начале handleGenerate, пишет в localStorage через
	// `savePendingSnapshot`, и на успехе вызывает `saveToHistory` напрямую.
	// Мы передаём `addGeneration` и вычисленные `isVideo`/`isAudio` — всё
	// остальное flow собирает сам из переданных полей.
	const motionRisk = useMotionPrecheck(
		selectedModel,
		uploads.uploadedVideo?.url ?? null,
		uploads.selectedVideoDuration,
		settings.characterOrientation,
	)

	const flow = useGenerationFlow({
		selectedModel,
		prompt,
		// Тумблер «Черновик» мог остаться включённым в сохранённых настройках, а сервер/вход его уже не принимают:
		// в запрос он тогда не идёт (иначе отказ «черновики недоступны» вместо обычной генерации).
		settings: settings.draft && !draftAvailable ? { ...settings, draft: false } : settings,
		user,
		uploadedImage: uploads.uploadedImage,
		swapImage: uploads.swapImage,
		uploadedVideo: uploads.uploadedVideo,
		referenceVideo: uploads.referenceVideo,
		referenceImages: uploads.referenceImages,
		endImage: uploads.endImage,
		omniReferenceImages: uploads.omniReferenceImages,
		referenceVideos: uploads.referenceVideos,
		referenceAudios: uploads.referenceAudios,
		slotImages: uploads.slotImages,
		multiShots,
		selectedVideoDuration: uploads.selectedVideoDuration,
		motionRisk,
		requiresVideo,
		requiresImage,
		requiresTwoImages,
		supportsMultipleImages,
		isVideo,
		isAudio,
		isPresentation,
		sourceTrack,
		audioOperation,
		addGeneration,
		addNotification: addNotification as (message: string, type: string) => void,
		refreshUser,
		// После успешной audio-генерации сбрасываем source-track, чтобы
		// следующая генерация начиналась с чистого листа (не молча extend).
		onGenerationSuccess: () => {
			setSourceTrackState(null)
			setAudioOperation('generate')
		},
		expectedCredits: currentCost,
	})

	useEffect(() => {
		setErrorRef.current = flow.setError
	})

	const wasGeneratingRef = useRef(false)
	useEffect(() => {
		setIsGenerating(flow.isLoading)
		// Генерация завершилась (успех ИЛИ ошибка) → сразу синхронизируем баланс с
		// сервером. На провале бэк рефандит токены — без этого списание «висит» в
		// шапке до следующего поллинга (ложное ощущение «съело»).
		if (wasGeneratingRef.current && !flow.isLoading) void refreshUser()
		wasGeneratingRef.current = flow.isLoading
	}, [flow.isLoading, setIsGenerating, refreshUser])

	// Лоадинг-пилюля на ИСХОДНОМ треке во время генерации версии: продление/перепеть/
	// +вокал/+муз. идут через виджет с pinned sourceTrack. Помечаем source в стор на
	// время isLoading → список+кабинет рисуют спиннер. Свежая генерация (без source) — не метим.
	useEffect(() => {
		if (!flow.isLoading || !sourceTrack) return
		// Помечаем source на время генерации; cleanup снимает метку при завершении /
		// смене источника / размонтировании — без ручного ref, без залипшего спиннера.
		const id = sourceTrack.id
		genVersionStore.start(id)
		return () => genVersionStore.end(id)
	}, [flow.isLoading, sourceTrack])

	// ── resetAll ──────────────────────────────────────────────
	const resetAll = useCallback(() => {
		// Метка ПРОГРАММНОЙ очистки каналов: страж в useSceneApply не
		// принимает её за ручное удаление фото — «липкий» персонаж переживает
		// сброс и переприменяется (жалоба владельца «персонаж скидывается»).
		markSceneChannelsReset()
		uploads.resetUploads()
		flow.resetResult()
		setMultiShots([])
		setPromptState('')
		setSourceTrackState(null)
		setAudioOperation('generate')
		setCtxPrompt('')
		setNegativePromptOpen(false)
		setShowStyleHint(false)
		localStorage.removeItem(GENERATE_PROMPT_KEY)
		localStorage.removeItem(PERSISTED_UPLOADS_KEY)
		if (selectedModel) resetSettings(selectedModel)
	}, [uploads.resetUploads, flow.resetResult, selectedModel, resetSettings, setCtxPrompt]) // eslint-disable-line react-hooks/exhaustive-deps

	// ── Context value ─────────────────────────────────────────
	const value: GenerateContextValue = {
		selectedModel,
		setSelectedModel,
		prompt,
		setPrompt,
		settings,
		setSettings,
		updateSetting,
		userUpdateSetting,
		resetSettings,
		isUploading: uploads.isUploading,
		setIsUploading: uploads.setIsUploading,
		hasPendingUploads: uploads.hasPendingUploads,
		isDragging: uploads.isDragging,
		uploadedImage: uploads.uploadedImage,
		referenceImages: uploads.referenceImages,
		slotImages: uploads.slotImages,
		photoSlotsEngaged: uploads.photoSlotsEngaged,
		handleSlotImageUpload: uploads.handleSlotImageUpload,
		removeSlotImage: uploads.removeSlotImage,
		setSlotImagePending: uploads.setSlotImagePending,
		setActiveSlotNums: uploads.setActiveSlotNums,
		swapImage: uploads.swapImage,
		uploadedVideo: uploads.uploadedVideo,
		endImage: uploads.endImage,
		omniReferenceImages: uploads.omniReferenceImages,
		referenceVideo: uploads.referenceVideo,
		referenceVideos: uploads.referenceVideos,
		referenceAudios: uploads.referenceAudios,
		selectedVideoDuration: uploads.selectedVideoDuration,
		motionRisk,
		fileInputRef: uploads.fileInputRef,
		swapInputRef: uploads.swapInputRef,
		videoInputRef: uploads.videoInputRef,
		endImageInputRef: uploads.endImageInputRef,
		refImagesInputRef: uploads.refImagesInputRef,
		refVideoInputRef: uploads.refVideoInputRef,
		refVideosInputRef: uploads.refVideosInputRef,
		refAudiosInputRef: uploads.refAudiosInputRef,
		handleImageUpload: uploads.handleImageUpload,
		handleMultipleImageUpload: uploads.handleMultipleImageUpload,
		handleSwapImageUpload: uploads.handleSwapImageUpload,
		handleVideoUpload: uploads.handleVideoUpload,
		handleEndImageUpload: uploads.handleEndImageUpload,
		handleOmniRefImageUpload: uploads.handleOmniRefImageUpload,
		handleReferenceVideoUpload: uploads.handleReferenceVideoUpload,
		handleReferenceVideosUpload: uploads.handleReferenceVideosUpload,
		handleReferenceAudiosUpload: uploads.handleReferenceAudiosUpload,
		audioTrimFile: uploads.audioTrimFile,
		applyTrimmedAudio: uploads.applyTrimmedAudio,
		cancelAudioTrim: uploads.cancelAudioTrim,
		videoTrimUrl: uploads.videoTrimUrl,
		applyTrimmedVideo: uploads.applyTrimmedVideo,
		cancelVideoTrim: uploads.cancelVideoTrim,
		applyDefaultReferenceVideo: uploads.applyDefaultReferenceVideo,
		applyDefaultReferenceVideos: uploads.applyDefaultReferenceVideos,
		applyDefaultReferenceAudio: uploads.applyDefaultReferenceAudio,
		applyDefaultUploadedVideo: uploads.applyDefaultUploadedVideo,
		applyDefaultUploadedImage: uploads.applyDefaultUploadedImage,
		dragProps: uploads.dragProps,
		setUploadedImage: uploads.setUploadedImage,
		setSwapImage: uploads.setSwapImage,
		setUploadedVideo: uploads.setUploadedVideo,
		setEndImage: uploads.setEndImage,
		setReferenceImages: uploads.setReferenceImages,
		setOmniReferenceImages: uploads.setOmniReferenceImages,
		setReferenceVideo: uploads.setReferenceVideo,
		setReferenceVideos: uploads.setReferenceVideos,
		setReferenceAudios: uploads.setReferenceAudios,
		setSelectedVideoDuration: uploads.setSelectedVideoDuration,
		// Обёртка с меткой программного сброса (см. resetAll): applyCard чистит
		// слоты под новую карточку — липкий персонаж не должен слетать.
		resetUploads: () => {
			markSceneChannelsReset()
			uploads.resetUploads()
		},
		// Смена участника мульти-тренда (23.10): референсы карточки заново; каналы, запертые у новой карточки, —
		// целиком (фото/видео юзера там невидимо и в модель идти не должно); остальное юзера остаётся.
		resetCardRefs: () => {
			const pin = getAttachedStyleCard()
			uploads.resetCardRefs({
				clearImage: !!(pin?.userImageLocked && pin?.referenceImageUrl),
				clearVideo: !!pin?.userVideoLocked,
				clearAudio: !!pin?.userAudioLocked,
			})
		},
		restoreInputs: uploads.restoreInputs,
		uploadsEpoch: uploads.uploadsEpoch,
		currentCost,
		costPending,
		costIssue,
		draftInfo,
		draftAvailable,
		isLoading: flow.isLoading,
		error: flow.error,
		result: flow.result,
		resultUrls: flow.resultUrls,
		resultImageUrls: flow.resultImageUrls,
		lastRequestId: flow.lastRequestId,
		userRating: flow.userRating,
		handleGenerate: flow.handleGenerate,
		detachGeneration: flow.detachGeneration,
		resetResult: flow.resetResult,
		restoreResult: flow.restoreResult,
		setError: flow.setError,
		clearError: flow.clearError,
		setResult: flow.setResult,
		setUserRating: flow.setUserRating,
		negativePromptOpen,
		setNegativePromptOpen,
		multiShots,
		setMultiShots,
		showStyleHint,
		setShowStyleHint,
		isVideo,
		isAudio,
		isPresentation,
		requiresVideo,
		requiresImage,
		supportsImageInput,
		requiresTwoImages,
		supportsMultipleImages,
		sourceTrack,
		audioOperation,
		setSourceTrack,
		resetAll,
	}

	return <GenerateContext.Provider value={value}>{children}</GenerateContext.Provider>
}
